import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Navigation, Heart, Share2, BarChart3, Lock } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api } from "../lib/api";

const SRC_LABEL = {
  recherche: "Recherche",
  accueil: "Page d'accueil",
  favoris: "Favoris",
  interne: "Navigation interne",
  externe: "Site externe",
  direct: "Lien direct / QR",
};

const fmtDay = (iso) => {
  const d = new Date(iso + "T00:00:00");
  return `${d.getDate()}/${d.getMonth() + 1}`;
};

const Tile = ({ Icon, color, value, label, testid }) => (
  <div className="bg-cream-bg rounded-xl p-4" data-testid={testid}>
    <Icon className={color} size={18} />
    <p className="font-display font-extrabold text-2xl text-urban-teal mt-1">{value}</p>
    <p className="text-xs text-urban-teal/60">{label}</p>
  </div>
);

export const AnalyticsPanel = ({ isPro, isProPlus }) => {
  const [range, setRange] = useState(7);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!isPro) return;
    api.get(`/me/analytics?days=${range}`).then((r) => setData(r.data)).catch(() => {});
  }, [range, isPro]);

  if (!isPro) {
    return (
      <div className="bg-white rounded-2xl shadow-warm p-6 mt-6 relative overflow-hidden" data-testid="analytics-locked">
        <h2 className="font-display font-bold text-lg text-urban-teal flex items-center gap-2 mb-1">
          <BarChart3 size={18} className="text-street-gold" /> Analytics
          <span className="text-[10px] bg-urban-teal text-white px-2 py-0.5 rounded-full uppercase tracking-wide">Pro</span>
        </h2>
        <p className="text-sm text-urban-teal/60 mb-4">Suivez vos vues jour par jour, les clics itinéraire, les favoris gagnés et l'origine de vos visiteurs.</p>
        <div className="rounded-xl border-2 border-dashed border-cream-bg p-6 flex flex-col items-center text-center gap-2">
          <Lock className="text-urban-teal/40" size={26} />
          <p className="text-sm text-urban-teal/70 font-semibold">Débloquez vos statistiques réelles avec l'offre Pro.</p>
          <Link to="/tarifs" className="rounded-full bg-street-gold text-white font-bold text-sm px-5 py-2 mt-1" data-testid="analytics-upgrade-btn">Passer en Pro</Link>
        </div>
      </div>
    );
  }

  const totals = data?.totals || { view: 0, itinerary: 0, share: 0, favorite: 0 };
  const series = (data?.series || []).map((s) => ({ ...s, label: fmtDay(s.date) }));
  const sources = data?.sources || [];
  const totalSrc = sources.reduce((s, x) => s + x.count, 0) || 1;
  const tickInterval = range === 7 ? 0 : Math.max(1, Math.floor(series.length / 6));

  return (
    <div className="bg-white rounded-2xl shadow-warm p-6 mt-6" data-testid="analytics-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h2 className="font-display font-bold text-lg text-urban-teal flex items-center gap-2">
          <BarChart3 size={18} className="text-street-gold" /> Analytics
          <span className="text-[10px] bg-street-gold text-white px-2 py-0.5 rounded-full uppercase tracking-wide">{isProPlus ? "Pro+" : "Pro"}</span>
        </h2>
        <div className="flex bg-cream-bg rounded-full p-1" data-testid="analytics-range">
          {[7, 90].map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors active:scale-95 ${range === r ? "bg-white text-urban-teal shadow-sm" : "text-urban-teal/50"}`}
              data-testid={`analytics-range-${r}`}
            >
              {r === 7 ? "7 jours" : "90 jours"}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
        <Tile Icon={Eye} color="text-street-gold" value={totals.view} label={`Vues (${range} j)`} testid="analytics-total-views" />
        <Tile Icon={Navigation} color="text-fresh-mint" value={totals.itinerary} label="Clics itinéraire" testid="analytics-total-itinerary" />
        <Tile Icon={Heart} color="text-coral-twist" value={totals.favorite} label="Favoris gagnés" testid="analytics-total-favorite" />
        <Tile Icon={Share2} color="text-urban-teal" value={totals.share} label="Partages" testid="analytics-total-share" />
      </div>

      <div className="mt-6" data-testid="analytics-chart">
        <p className="text-xs font-bold uppercase text-urban-teal/50 mb-2">Vues par jour</p>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={series} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#FF9F1C" stopOpacity={0.35} />
                <stop offset="95%" stopColor="#FF9F1C" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#FBF7EA" vertical={false} />
            <XAxis dataKey="label" interval={tickInterval} tick={{ fontSize: 11, fill: "#0E3F5B99" }} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#0E3F5B99" }} axisLine={false} tickLine={false} width={30} />
            <Tooltip
              contentStyle={{ borderRadius: 12, border: "1px solid #FBF7EA", fontSize: 13 }}
              labelStyle={{ color: "#0E3F5B", fontWeight: 700 }}
              formatter={(v) => [v, "Vues"]}
            />
            <Area type="monotone" dataKey="views" stroke="#FF9F1C" strokeWidth={2.5} fill="url(#viewsGrad)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {isProPlus && (
        <div className="mt-6" data-testid="analytics-sources">
          <p className="text-xs font-bold uppercase text-urban-teal/50 mb-2">Origine des visites</p>
          {sources.length === 0 ? (
            <p className="text-sm text-urban-teal/50">Pas encore de données sur cette période.</p>
          ) : (
            <div className="space-y-2">
              {sources.map((s) => {
                const pct = Math.round((s.count / totalSrc) * 100);
                return (
                  <div key={s.source} data-testid={`analytics-source-${s.source}`}>
                    <div className="flex justify-between text-sm text-urban-teal/80 mb-1">
                      <span className="font-semibold">{SRC_LABEL[s.source] || s.source}</span>
                      <span>{s.count} · {pct}%</span>
                    </div>
                    <div className="h-2 bg-cream-bg rounded-full overflow-hidden">
                      <div className="h-full bg-fresh-mint rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
